import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Edit2, Trash2, RefreshCw, RotateCcw, KeyRound, Copy, Check, Download } from 'lucide-react';
import { nasAPI, vpnServersAPI, vpnWireguardAPI, vpnIpsecAPI } from '../api';
import { Card, Button, Badge, Table, Pagination, Modal, Input, Select, PageHeader, Alert, useSort } from '../components/UI';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: loading ? 'wait' : 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)',
    transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 160, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};


const EMPTY_FORM = { shortname: '', nasname: '', secret: '', type: 'other', ports: 0, coa_port: 3799, description: '', status: 'active', disconnect_user: false };

const SyncBadge = ({ status }) => {
  const map = { synced: { color: 'var(--accent-green)', bg: 'rgba(16,185,129,0.12)' }, pending: { color: 'var(--accent-orange)', bg: 'rgba(245,158,11,0.12)' }, error: { color: 'var(--accent-red)', bg: 'rgba(239,68,68,0.12)' } };
  const s = map[status] || { color: 'var(--text-muted)', bg: 'var(--bg-input)' };
  return <span style={{ fontSize: 11, padding: '2px 8px', background: s.bg, color: s.color, borderRadius: 20, border: `1px solid ${s.color}44` }}>{status || '—'}</span>;
};

const CopyBtn = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'inline-flex', verticalAlign: -2 }}>
      {copied ? <Check size={12} color="var(--accent-green)" /> : <Copy size={12} />}
    </button>
  );
};

const downloadFile = (filename, content) => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const NasMenuButton = ({ n, openMenuId, setOpenMenuId, onlyDeleted, syncingId, handleRestore, handleSync, openEdit, handleViewConfig, setDeleteConfirm, setDeletePermanent, isSuperadmin }) => {
  const btnRef = React.useRef(null);
  return (
    <div>
      <button ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === n.id ? null : n.id); }}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>
        ···
      </button>
      <DropdownPortal anchorRef={btnRef} open={openMenuId === n.id}>
        {onlyDeleted ? (
          <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(n); setOpenMenuId(null); }}>↩ Restore</MenuItem>
        ) : (<>
          <MenuItem onClick={() => { handleViewConfig(n); setOpenMenuId(null); }}><KeyRound size={13} style={{ marginRight: 6, verticalAlign: -2 }} />Lihat Config</MenuItem>
          <MenuItem loading={syncingId === n.id} onClick={() => { handleSync(n); setOpenMenuId(null); }}>🔄 Sync</MenuItem>
          <MenuItem onClick={() => { openEdit(n); setOpenMenuId(null); }}>✏️ Edit</MenuItem>
        </>)}
        {(!onlyDeleted || isSuperadmin) && (<>
          <div style={{ borderTop: '1px solid var(--border)' }} />
          <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(n); setDeletePermanent(false); setOpenMenuId(null); }}>🗑 Hapus</MenuItem>
        </>)}
      </DropdownPortal>
    </div>
  );
};

export default function NasDevices() {
  const { selectedTenant } = useTenant();
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [nas, setNas] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [configTarget, setConfigTarget] = useState(null); // hasil GET .../nas/:id (reveal_secrets: true)
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [bulkUpdateModal, setBulkUpdateModal] = useState(false);
  const [bulkUpdateForm, setBulkUpdateForm] = useState({ secret: '', type: '', ports: '', description: '' });
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [perPage, setPerPage] = useState(20);

  // ── Kombo "sekalian bikin VPN" (cuma buat CREATE, bukan edit) ──
  const [useVpn, setUseVpn] = useState(false);
  const [vpnType, setVpnType] = useState('wireguard');
  const [vpnServers, setVpnServers] = useState([]);
  const [vpnServerId, setVpnServerId] = useState('');
  const [vpnIp, setVpnIp] = useState('');
  const [vpnAvailableIps, setVpnAvailableIps] = useState([]);
  const [vpnDns, setVpnDns] = useState('');
  const [vpnAllowedIps, setVpnAllowedIps] = useState('');
  const [vpnKeepalive, setVpnKeepalive] = useState(25);
  const [vpnPsk, setVpnPsk] = useState(true);
  const [vpnPassword, setVpnPassword] = useState('');
  const [comboResult, setComboResult] = useState(null); // { nas, vpn } dari response create+vpn

  useEffect(() => {
    if (!useVpn) return;
    vpnServersAPI.list({ type: vpnType, status: 'active' })
      .then(r => { const list = r.vpn_servers || []; setVpnServers(list); setVpnServerId(list[0]?.id || ''); })
      .catch(() => setVpnServers([]));
    setVpnIp(''); setVpnAvailableIps([]);
  }, [useVpn, vpnType]);

  useEffect(() => {
    if (!useVpn || !vpnServerId || !selectedTenant) { setVpnAvailableIps([]); return; }
    const fetcher = vpnType === 'wireguard' ? vpnWireguardAPI.availableIps : vpnIpsecAPI.availableIps;
    fetcher(selectedTenant, vpnServerId)
      .then(r => setVpnAvailableIps(r.available || []))
      .catch(() => setVpnAvailableIps([]));
  }, [useVpn, vpnType, vpnServerId, selectedTenant]);

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  const { sort, onSortChange, sortParam } = useSort();

  const load = useCallback(async () => {
    if (!selectedTenant) { setNas([]); return; }
    setLoading(true); setError(null);
    try {
      const params = { page, per_page: perPage };
      if (onlyDeleted) params.only_deleted = true;
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter; // bisa comma-separated
      if (sortParam) params.sort = sortParam;
      const res = await nasAPI.list(selectedTenant, params);
      setNas(res.nas_devices || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [selectedTenant, page, q, statusFilter, onlyDeleted, perPage, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [selectedTenant, onlyDeleted]);

  const openCreate = () => {
    if (!selectedTenant) { setError('Pilih tenant terlebih dahulu.'); return; }
    setEditTarget(null); setForm(EMPTY_FORM); setFormError(null); setModalOpen(true);
    setUseVpn(false); setVpnType('wireguard'); setVpnServerId(''); setVpnIp('');
    setVpnDns(''); setVpnAllowedIps(''); setVpnKeepalive(25); setVpnPsk(true); setVpnPassword('');
  };
  const openEdit = (n) => {
    setEditTarget(n);
    setForm({ shortname: n.shortname || '', nasname: n.nasname || '', secret: '', type: n.type || 'other', ports: n.ports ?? 0, coa_port: n.coa_port ?? 3799, description: n.description || '', status: n.status || 'active', disconnect_user: false });
    setFormError(null); setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setFormError(null);
    try {
      const payload = { ...form };
      if (editTarget && !payload.secret) delete payload.secret;
      if (!editTarget) delete payload.disconnect_user;

      if (!editTarget && useVpn) {
        if (!vpnServerId) { setFormError('Pilih VPN server dulu.'); setSaving(false); return; }
        if (vpnType === 'ipsec' && !vpnIp) { setFormError('static_ip wajib dipilih buat kombo NAS+IPsec.'); setSaving(false); return; }
        payload.vpn = {
          type: vpnType,
          vpn_server_id: vpnServerId,
          peer_name: form.shortname || form.nasname || undefined,
          allowed_ips: vpnAllowedIps || undefined,
          ...(vpnType === 'wireguard'
            ? { ip_address: vpnIp || undefined, dns: vpnDns || undefined, keepalive: vpnKeepalive || undefined, psk: vpnPsk }
            : { static_ip: vpnIp, password: vpnPassword || undefined }),
        };
      }

      if (editTarget) {
        await nasAPI.update(selectedTenant, editTarget.id, payload);
        setModalOpen(false); setSuccess('NAS diperbarui.'); load();
      } else {
        const res = await nasAPI.create(selectedTenant, payload);
        setModalOpen(false);
        setSuccess(payload.vpn ? 'NAS & VPN sekalian dibuat.' : 'NAS ditambahkan.');
        if (payload.vpn) setComboResult(res);
        load();
      }
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await nasAPI.deletePermanent(selectedTenant, deleteConfirm.id);
      else await nasAPI.delete(selectedTenant, deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? 'NAS dihapus permanen.' : 'NAS dihapus.'); load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (n) => {
    try { await nasAPI.restore(selectedTenant, n.id); setSuccess('NAS dipulihkan.'); load(); }
    catch (e) { setError(e.message); }
  };

  const handleBulkDelete = async () => {
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => nasAPI.deletePermanent(selectedTenant, id)));
      else await Promise.all(selected.map(id => nasAPI.delete(selectedTenant, id)));
      setSuccess(`${selected.length} NAS ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    try {
      await Promise.all(selected.map(id => nasAPI.restore(selectedTenant, id)));
      setSuccess(`${selected.length} NAS dipulihkan.`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const handleBulkSync = async () => {
    setBulkSyncing(true);
    try { await Promise.all(selected.map(id => nasAPI.sync(selectedTenant, id))); setSuccess(`${selected.length} NAS di-sync.`); setSelected([]); }
    catch (e) { setError(e.message); } finally { setBulkSyncing(false); }
  };

  const handleBulkUpdate = async () => {
    setBulkUpdating(true);
    try {
      const payload = {};
      if (bulkUpdateForm.secret) payload.secret = bulkUpdateForm.secret;
      if (bulkUpdateForm.type) payload.type = bulkUpdateForm.type;
      if (bulkUpdateForm.ports !== '') payload.ports = Number(bulkUpdateForm.ports);
      if (bulkUpdateForm.description) payload.description = bulkUpdateForm.description;
      if (Object.keys(payload).length === 0) { setError('Pilih minimal satu field.'); setBulkUpdating(false); return; }
      await Promise.all(selected.map(id => nasAPI.update(selectedTenant, id, payload)));
      setSuccess(`${selected.length} NAS diperbarui.`); setSelected([]); setBulkUpdateModal(false);
      setBulkUpdateForm({ secret: '', type: '', ports: '', description: '' }); load();
    } catch (e) { setError(e.message); } finally { setBulkUpdating(false); }
  };

  const handleSync = async (n) => {
    setSyncingId(n.id);
    try { await nasAPI.sync(selectedTenant, n.id); setSuccess('NAS di-sync.'); load(); }
    catch (e) { setError(e.message); } finally { setSyncingId(null); }
  };

  const handleViewConfig = async (n) => {
    try { const res = await nasAPI.get(selectedTenant, n.id); setConfigTarget(res); }
    catch (e) { setError(e.message); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const columns = [
    { key: 'shortname', label: 'Short Name', sortable: true, render: v => <span className="mono" style={{ color: 'var(--text-mono)', fontSize: 12 }}>{v}</span> },
    { key: 'nasname', label: 'IP / Hostname', sortable: true, render: v => <span className="mono" style={{ fontSize: 12 }}>{v}</span> },
    { key: 'type', label: 'Type', sortable: true, render: v => <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4, border: '1px solid var(--border)' }}>{v}</span> },
    { key: 'ports', label: 'Ports', render: v => <span className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v ?? 0}</span> },
    { key: 'coa_port', label: 'CoA Port', render: v => <span className="mono" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{v ?? '—'}</span> },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'sync_status', label: 'Sync', sortable: true, render: v => <SyncBadge status={v} /> },
    {
      key: 'id', label: '', render: (_, n) => (
        <NasMenuButton n={n} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId} onlyDeleted={onlyDeleted} syncingId={syncingId} handleRestore={handleRestore} handleSync={handleSync} openEdit={openEdit} handleViewConfig={handleViewConfig} setDeleteConfirm={setDeleteConfirm} setDeletePermanent={setDeletePermanent} isSuperadmin={isSuperadmin} />
      )
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="NAS Devices"
        subtitle={`${total} perangkat${onlyDeleted ? ' (terhapus)' : ''}`}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {selected.length > 0 && (<>
              {!onlyDeleted && <>
                <Button variant="ghost" loading={bulkSyncing} onClick={handleBulkSync}><RefreshCw size={13} /> Sync {selected.length}</Button>
                <Button variant="secondary" onClick={() => setBulkUpdateModal(true)}><Edit2 size={13} /> Update {selected.length}</Button>
              </>}
              {onlyDeleted && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
              {(!onlyDeleted || isSuperadmin) && (
                <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}><Trash2 size={13} /> Hapus {selected.length}</Button>
              )}
            </>)}
            <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
              {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
            </Button>
            <Button onClick={openCreate} disabled={!selectedTenant}><Plus size={14} /> Tambah NAS</Button>
          </div>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari NAS…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 220 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={nas} loading={loading} sort={sort} onSortChange={onSortChange}
          emptyMessage={selectedTenant ? (onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada NAS device') : 'Pilih tenant terlebih dahulu'}
          selected={selected} onSelect={setSelected} />
        <div style={{ padding: '0 16px' }}><Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} /></div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit NAS' : 'Tambah NAS'} width={!editTarget && useVpn ? 860 : 460}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Kolom kiri: form NAS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: '1 1 360px', minWidth: 300 }}>
            <Input label="Short Name *" value={form.shortname} onChange={e => f('shortname', e.target.value)} placeholder="router-jkt-01" />

            {!editTarget && (
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 500 }}>
                  <input type="checkbox" checked={useVpn} onChange={e => setUseVpn(e.target.checked)} style={{ accentColor: 'var(--accent-blue)' }} />
                  <span>Gunakan dan buat VPN</span>
                </label>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  IP / Hostname NAS akan otomatis diisi dari IP VPN yang dibuat.
                </p>
              </div>
            )}

            <div>
              <Input
                label={useVpn ? 'IP / Hostname (otomatis dari IP VPN)' : 'IP / Hostname *'}
                value={form.nasname} onChange={e => f('nasname', e.target.value)}
                placeholder={useVpn ? 'kosongkan, diisi otomatis dari IP VPN' : '192.168.1.1'}
                disabled={useVpn}
              />
            </div>
            <Input label="Secret *" type="password" value={form.secret} onChange={e => f('secret', e.target.value)} placeholder={editTarget ? '(kosong = tidak berubah)' : 'shared secret'} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Select label="Type" value={form.type} onChange={e => f('type', e.target.value)}>
                <option value="other">other</option><option value="cisco">cisco</option>
                <option value="mikrotik">mikrotik</option><option value="juniper">juniper</option><option value="huawei">huawei</option>
              </Select>
              <Input label="Ports" type="number" value={form.ports} onChange={e => f('ports', +e.target.value)} />
              <Input label="CoA Port" type="number" value={form.coa_port} onChange={e => f('coa_port', +e.target.value)} />
              <Select label="Status" value={form.status} onChange={e => f('status', e.target.value)}>
                <option value="active">Active</option><option value="inactive">Inactive</option>
              </Select>
            </div>
            <Input label="Description" value={form.description} onChange={e => f('description', e.target.value)} />
            {editTarget && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px 0' }}>
                <input type="checkbox" checked={form.disconnect_user || false} onChange={e => f('disconnect_user', e.target.checked)} style={{ accentColor: 'var(--accent-orange)' }} />
                <span>Disconnect user yang sedang online setelah update</span>
              </label>
            )}
          </div>

          {/* Kolom kanan: form VPN, muncul di samping kalau useVpn dicentang */}
          {!editTarget && useVpn && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: '1 1 320px', minWidth: 280 }}>
              <Select label="Tipe VPN" value={vpnType} onChange={e => setVpnType(e.target.value)}>
                <option value="wireguard">WireGuard</option>
                <option value="ipsec">L2TP/IPsec</option>
              </Select>
              <Select label="VPN Server *" value={vpnServerId} onChange={e => setVpnServerId(+e.target.value)}>
                <option value="">— pilih server —</option>
                {vpnServers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.pool})</option>)}
              </Select>

              {vpnType === 'wireguard' ? (
                <Select label="IP Address" value={vpnIp} onChange={e => setVpnIp(e.target.value)}>
                  <option value="">— Auto-assign —</option>
                  {vpnAvailableIps.map(ip => <option key={ip} value={ip}>{ip}</option>)}
                </Select>
              ) : (
                <Select label="Static IP *" value={vpnIp} onChange={e => setVpnIp(e.target.value)}>
                  <option value="">— pilih IP statis —</option>
                  {vpnAvailableIps.map(ip => <option key={ip} value={ip}>{ip}</option>)}
                </Select>
              )}

              {vpnType === 'wireguard' ? (<>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Input label="DNS" value={vpnDns} onChange={e => setVpnDns(e.target.value)} placeholder="default server" />
                  <Input label="Keepalive (detik)" type="number" value={vpnKeepalive} onChange={e => setVpnKeepalive(+e.target.value)} />
                </div>
                <Input label="Allowed IPs (routes client)" value={vpnAllowedIps} onChange={e => setVpnAllowedIps(e.target.value)} placeholder="default server" />
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={vpnPsk} onChange={e => setVpnPsk(e.target.checked)} style={{ accentColor: 'var(--accent-blue)' }} />
                  <span>Pakai Preshared Key (rekomendasi)</span>
                </label>
              </>) : (<>
                <Input label="Password" value={vpnPassword} onChange={e => setVpnPassword(e.target.value)} placeholder="kosong = auto-generate" />
                <Input label="Allowed IPs (routes client)" value={vpnAllowedIps} onChange={e => setVpnAllowedIps(e.target.value)} placeholder="default server" />
              </>)}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>{editTarget ? 'Simpan' : 'Tambah NAS'}</Button>
        </div>
      </Modal>

      {/* Hasil kombo NAS + VPN */}
      <Modal open={!!comboResult} onClose={() => setComboResult(null)} title="NAS & VPN Berhasil Dibuat" width={560}>
        {comboResult && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 12, color: 'var(--accent-orange)' }}>
              ⚠️ Private key/password VPN cuma ditampilkan sekali di sini. Simpan/download sekarang.
            </p>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                Config NAS ({comboResult.nas?.nasname}) <CopyBtn value={comboResult.nas?.config} />
              </div>
              {comboResult.nas?.config ? (
                <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{comboResult.nas.config}</pre>
              ) : (
                <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Config NAS belum bisa dibuat (tenant/NAS belum ke-pin ke radius server).</p>
              )}
            </div>
            {comboResult.vpn && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                  Config VPN ({comboResult.vpn.name || comboResult.vpn.username}) <CopyBtn value={comboResult.vpn.config} />
                </div>
                <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{comboResult.vpn.config}</pre>
                {comboResult.vpn.config_routeros && (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', margin: '10px 0 4px' }}>Script Mikrotik RouterOS (VPN) <CopyBtn value={comboResult.vpn.config_routeros} /></div>
                    <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{comboResult.vpn.config_routeros}</pre>
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {comboResult.nas?.config && <Button variant="secondary" onClick={() => downloadFile(`${comboResult.nas.shortname || comboResult.nas.nasname}-radius.rsc`, comboResult.nas.config)}><Download size={13} /> Download NAS</Button>}
              {comboResult.vpn?.config && <Button variant="secondary" onClick={() => downloadFile(`${comboResult.vpn.name || comboResult.vpn.username}-vpn.conf`, comboResult.vpn.config)}><Download size={13} /> Download VPN</Button>}
              <Button onClick={() => setComboResult(null)}>Selesai</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Config RouterOS */}
      <Modal open={!!configTarget} onClose={() => setConfigTarget(null)} title={`Config NAS — ${configTarget?.shortname || configTarget?.nasname || ''}`} width={560}>
        {configTarget && (
          configTarget.config ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <p style={{ fontSize: 12, color: 'var(--accent-orange)' }}>
                ⚠️ Config ini mengandung secret NAS dalam bentuk plain text — jangan share sembarangan.
              </p>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Script Mikrotik RouterOS <CopyBtn value={configTarget.config} /></div>
                <pre style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{configTarget.config}</pre>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={() => downloadFile(`${configTarget.shortname || configTarget.nasname}-radius.rsc`, configTarget.config)}><Download size={13} /> Download</Button>
                <Button onClick={() => setConfigTarget(null)}>Selesai</Button>
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              NAS/tenant ini belum ke-pin ke radius server manapun, jadi config gak bisa dibuat.
              Set <strong>Radius Server</strong> di tenant atau NAS ini dulu.
            </p>
          )
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus NAS" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus NAS <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.shortname}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ NAS ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk Delete */}
      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk NAS" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Hapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} NAS</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ NAS ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkDeleteConfirm(false)}>Batal</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk Update */}
      <Modal open={bulkUpdateModal} onClose={() => setBulkUpdateModal(false)} title={`Bulk Update — ${selected.length} NAS`} width={420}>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Field kosong tidak akan diubah. <span style={{ color: 'var(--accent-orange)' }}>✗ Short Name & IP tidak bisa di-bulk update.</span></p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Secret (baru)" type="password" value={bulkUpdateForm.secret} onChange={e => setBulkUpdateForm(f => ({ ...f, secret: e.target.value }))} placeholder="kosong = tidak diubah" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Type" value={bulkUpdateForm.type} onChange={e => setBulkUpdateForm(f => ({ ...f, type: e.target.value }))}>
              <option value="">— Tidak diubah —</option><option value="other">other</option>
              <option value="cisco">cisco</option><option value="mikrotik">mikrotik</option>
              <option value="juniper">juniper</option><option value="huawei">huawei</option>
            </Select>
            <Input label="Ports" type="number" value={bulkUpdateForm.ports} onChange={e => setBulkUpdateForm(f => ({ ...f, ports: e.target.value }))} placeholder="kosong = tidak diubah" />
          </div>
          <Input label="Description" value={bulkUpdateForm.description} onChange={e => setBulkUpdateForm(f => ({ ...f, description: e.target.value }))} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setBulkUpdateModal(false)}>Batal</Button>
          <Button onClick={handleBulkUpdate} loading={bulkUpdating}>Update {selected.length} NAS</Button>
        </div>
      </Modal>
    </div>
  );
}